// components/Compliance/EmployeeGroupsList.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Plus, Edit2, Trash2, FilterX } from 'lucide-react';
import {
  PageHeader, AppButton, ConfirmModal, StatusBadge, FilterToolbar, AppDataTable,
  ActionMenu, ActionMenuItem, ActionMenuDivider,
  applyColumnFilters, sortRows, isColumnFilterActive,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { listGroups, deleteGroup } from '../../utils/complianceApi';

/**
 * EmployeeGroupsList — the audiences a mandatory-training campaign targets.
 *
 * The table is `ui/AppDataTable` with the header controls on, the same shape as
 * `/sa/institutes`: `listGroups` returns every group in one response, so
 * filtering, sorting and paging all happen in the browser over the whole list.
 *
 * ⚠️ This file no longer imports `_compliance.scss`. The sheet stays — five
 * sibling Compliance components still render its classes, and so does
 * `SuperAdmin/ZoomHosts/CapacityPlannerTab`, which imports no stylesheet of its
 * own and is styled only because some Compliance page put this one in the
 * bundle. Nothing here may delete it.
 */

const PAGE_SIZE = 25;

const DASH = <span className="tw-text-content-subtle">—</span>;

const TYPE_FILTER_OPTIONS = [
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
];

export default function EmployeeGroupsList() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const showToast = useCallback(
    (message, type = 'success') => setToast({ show: true, message, type }),
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listGroups();
      setGroups(data?.groups || []);
    } catch {
      showToast('Failed to load employee groups.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filter: 'text',
      filterValue: (g) => g.name || '',
      render: (g) => <span className="tw-font-medium tw-text-content">{g.name}</span>,
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      filter: { type: 'set', options: TYPE_FILTER_OPTIONS },
      filterValue: (g) => g.type || 'static',
      render: (g) => (
        <StatusBadge status={g.type === 'dynamic' ? 'pending' : 'active'}>
          {g.type}
        </StatusBadge>
      ),
    },
    {
      key: 'members',
      label: 'Members',
      align: 'right',
      sortable: true,
      // A dynamic group has no countable membership until the filter runs, so
      // it sorts as blank (last) rather than as zero, which would file it below
      // every real group.
      sortValue: (g) => (g.type === 'static' ? (g.memberIds || []).length : null),
      render: (g) => (g.type === 'static'
        ? <span className="tw-tabular-nums">{(g.memberIds || []).length}</span>
        : <span className="tw-text-content-subtle">dynamic (filter)</span>),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      filter: 'date',
      filterValue: (g) => g.updatedAt || null,
      // ⚠️ IST explicitly. The box is UTC and a bare toLocaleDateString names
      // the previous day for anything saved after 05:30 IST.
      render: (g) => (g.updatedAt
        ? new Date(g.updatedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })
        : DASH),
    },
    {
      key: 'actions',
      label: '',
      width: 48,
      pinned: 'right',
      resizable: false,
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also navigates away.
      render: (g) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${g.name}`}>
            <ActionMenuItem onClick={() => navigate(`/compliance/employee-groups/${g._id}`)}>
              <Edit2 size={14} /> Edit
            </ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => setConfirm(g)}>
              <Trash2 size={14} /> Delete
            </ActionMenuItem>
          </ActionMenu>
        </div>
      ),
    },
  ], [navigate]);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);

  const visible = useMemo(
    () => sortRows(applyColumnFilters(groups, columns, columnFilters), columns, sort),
    [groups, columns, columnFilters, sort],
  );

  // A filter or a sort can leave the current page past the end of the results.
  useEffect(() => { setPage(1); }, [columnFilters, sort]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page],
  );

  const onDelete = async () => {
    if (!confirm) return;
    try {
      setDeleting(true);
      await deleteGroup(confirm._id);
      setConfirm(null);
      showToast('Employee group deleted.');
      load();
    } catch (err) {
      // Without this the dialog sat open for ever on a failed delete and
      // nothing on screen said why.
      showToast(err?.response?.data?.message || 'Could not delete the group.', 'error');
      setConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        title="Employee Groups"
        icon={Briefcase}
        backButton
      />

      <AppDataTable
        fill={false}
        resizable
        storageKey="compliance-employee-groups"
        toolbar={
          <FilterToolbar
            flush
            actions={
              <AppButton size="sm" onClick={() => navigate('/compliance/employee-groups/new')}>
                <Plus size={16} /> New Group
              </AppButton>
            }
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {visible.length === groups.length
                ? `${groups.length} group${groups.length === 1 ? '' : 's'}`
                : `${visible.length} of ${groups.length}`}
            </span>
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={pageRows}
        rowKey={(g) => g._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            // Cleared filters are REMOVED, not stored as undefined — a key
            // sitting there holding nothing would keep "Clear filters" on
            // screen after the last one was cleared.
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        onRowClick={(g) => navigate(`/compliance/employee-groups/${g._id}`)}
        emptyMessage={filtersOn
          ? 'No group matches these filters.'
          : 'No employee groups yet. Create one to target mandatory training campaigns.'}
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total: visible.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: 'groups',
        } : undefined}
      />

      <ConfirmModal
        show={!!confirm}
        title="Delete employee group?"
        message={confirm ? `"${confirm.name}" will be removed.` : ''}
        confirmText="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={onDelete}
        onHide={() => setConfirm(null)}
      />
    </div>
  );
}
